// src/services/cardNumberService.js
/**
 * Service pour gérer les numéros de carte de service
 * Les numéros sont sauvegardés dans localStorage et s'incrémentent automatiquement
 * Le numéro est réinitialisé chaque année
 * Gère également la liste des agents qui ont reçu/imprimé leur carte
 */

const STORAGE_KEY = 'service_card_numbers'
const CURRENT_YEAR = new Date().getFullYear()
const CARDS_FILE_PATH = '/data/cartes_service.json'

/**
 * Structure des données stockées:
 * {
 *   year: 2024,
 *   lastNumber: 5,
 *   agents: [
 *     {
 *       agentId: "123",
 *       id: "123",
 *       matricule: "MAT001",
 *       nom: "Doe",
 *       prenom: "John",
 *       sexe: "M",
 *       date_naissance: "1990-01-01",
 *       email: "john.doe@example.com",
 *       telephone: "+1234567890",
 *       direction: "Direction Technique",
 *       fonction: "Ingénieur",
 *       grade: "Grade A",
 *       cardNumber: 1,
 *       capturedDate: "2024-01-15T10:30:00Z"
 *     }
 *   ]
 * }
 */

/**
 * Initialise ou récupère les données de numérotation
 * Les données sont chargées depuis le fichier public/data/cartes_service.json
 * et synchronisées avec localStorage
 * @returns {Object} { year, lastNumber, agents }
 */
const getCardNumberData = () => {
  try {
    // Toujours lire depuis localStorage (qui est synchronisé avec le fichier)
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const data = JSON.parse(stored)
      // Si on change d'année, réinitialiser le compteur et la liste
      if (data.year !== CURRENT_YEAR) {
        const newData = { 
          year: CURRENT_YEAR, 
          lastNumber: 0,
          agents: []
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newData))
        return newData
      }
      // S'assurer que la liste des agents existe
      if (!data.agents) {
        data.agents = []
      }
      return data
    }
    
    // Si localStorage est vide, retourner des données par défaut
    // Le fichier sera chargé par loadCardsFromCentralFile() au démarrage
    const initialData = { 
      year: CURRENT_YEAR, 
      lastNumber: 0,
      agents: []
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(initialData))
    return initialData
  } catch (error) {
    console.error('Erreur lors de la lecture des numéros de carte:', error)
    // Retourner des valeurs par défaut en cas d'erreur
    return { 
      year: CURRENT_YEAR, 
      lastNumber: 0,
      agents: []
    }
  }
}

/**
 * Charge les données depuis le fichier JSON centralisé
 * @returns {Promise<Object|null>} { year, lastNumber, agents } ou null si erreur
 */
const loadCardsFromFile = async () => {
  try {
    const response = await fetch(CARDS_FILE_PATH)
    if (response.ok) {
      const data = await response.json()
      // Si on change d'année, réinitialiser le compteur et la liste
      if (data.year !== CURRENT_YEAR) {
        const newData = { 
          year: CURRENT_YEAR, 
          lastNumber: 0,
          agents: []
        }
        // Synchroniser avec localStorage
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newData))
        return newData
      }
      // S'assurer que la liste des agents existe
      if (!data.agents) {
        data.agents = []
      }
      // Synchroniser avec localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
      return data
    }
  } catch (error) {
    console.warn('⚠️ Impossible de charger depuis le fichier, utilisation de localStorage:', error)
  }
  return null
}

/**
 * Vérifie si un agent a déjà un numéro de carte assigné
 * @param {string|number} agentId - L'ID de l'agent
 * @returns {Object|null} Les informations de la carte de l'agent ou null
 */
export const getAgentCardInfo = (agentId) => {
  const data = getCardNumberData()
  const searchId = String(agentId)
  const agentInfo = data.agents.find(a => 
    String(a.agentId) === searchId || 
    String(a.id) === searchId
  )
  return agentInfo || null
}

/**
 * Obtient le numéro de carte d'un agent s'il en a déjà un
 * @param {string|number} agentId - L'ID de l'agent
 * @returns {number|null} Le numéro de carte ou null
 */
export const getAgentCardNumber = (agentId) => {
  const agentInfo = getAgentCardInfo(agentId)
  return agentInfo ? agentInfo.cardNumber : null
}

/**
 * Obtient le prochain numéro de carte disponible
 * @param {boolean} increment - Si true, incrémente le compteur après avoir obtenu le numéro
 * @returns {number} Le numéro de carte
 */
export const getNextCardNumber = (increment = false) => {
  const data = getCardNumberData()
  const nextNumber = data.lastNumber + 1
  
  if (increment) {
    // Sauvegarder le nouveau numéro
    const newData = { 
      ...data,
      year: CURRENT_YEAR, 
      lastNumber: nextNumber 
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newData))
  }
  
  return nextNumber
}

/**
 * Obtient ou assigne un numéro de carte pour un agent
 * Si l'agent a déjà un numéro, le retourne
 * Sinon, lui assigne le prochain numéro disponible
 * @param {Object} agent - L'objet agent avec toutes ses informations
 * @param {boolean} assignIfMissing - Si true, assigne un numéro si l'agent n'en a pas
 * @returns {number|null} Le numéro de carte de l'agent
 */
export const getOrAssignCardNumber = (agent, assignIfMissing = false) => {
  if (!agent || !agent.id) {
    console.error('Agent invalide pour l\'assignation de carte')
    return null
  }

  const data = getCardNumberData()
  
  // Vérifier si l'agent a déjà un numéro
  const existingCard = getAgentCardInfo(agent.id)
  if (existingCard) {
    return existingCard.cardNumber
  }

  // Si on ne doit pas assigner, retourner null
  if (!assignIfMissing) {
    return null
  }

  // Assigner un nouveau numéro
  const nextNumber = data.lastNumber + 1
  const agentCardInfo = {
    agentId: String(agent.id),
    id: String(agent.id),
    matricule: agent.matricule || '',
    nom: agent.nom || '',
    prenom: agent.prenom || '',
    sexe: agent.sexe || '',
    date_naissance: agent.date_naissance || '',
    email: agent.email || '',
    telephone: agent.telephone || '',
    direction: agent.direction || '',
    direction_id: agent.direction_id || null,
    fonction: agent.fonction || '',
    fonction_id: agent.fonction_id || null,
    grade: agent.grade || '',
    grade_id: agent.grade_id || null,
    adresse: agent.adresse || '',
    cardNumber: nextNumber,
    printed: false, // Sera marqué comme imprimé lors de la capture
    printedDate: null,
    assignedDate: new Date().toISOString()
  }

  // Ajouter l'agent à la liste
  data.agents.push(agentCardInfo)
  data.lastNumber = nextNumber

  // Sauvegarder
  const newData = {
    year: CURRENT_YEAR,
    lastNumber: nextNumber,
    agents: data.agents
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(newData))
  console.log(`💾 Agent assigné et sauvegardé:`, agentCardInfo)
  console.log(`💾 Données complètes sauvegardées:`, newData)

  return nextNumber
}

/**
 * Enregistre une carte capturée dans le fichier JSON
 * Si l'agent n'existe pas encore, le crée avec le numéro de carte
 * @param {string|number} agentId - L'ID de l'agent
 * @param {Object} agent - L'objet agent avec toutes ses informations
 * @param {number} cardNumber - Le numéro de carte (obligatoire)
 * @returns {boolean} True si l'enregistrement a réussi
 */
export const saveCapturedCard = (agentId, agent, cardNumber) => {
  try {
    if (!agentId) {
      console.error('❌ agentId manquant')
      return false
    }
    if (!agent) {
      console.error('❌ agent manquant')
      return false
    }
    if (!cardNumber || cardNumber === null) {
      console.error('❌ cardNumber manquant ou null')
      return false
    }
    
    console.log(`📝 Paramètres reçus: agentId=${agentId}, cardNumber=${cardNumber}, agent=`, agent)
    
    const data = getCardNumberData()
    
    // Utiliser l'ID de l'agent (priorité: agent.id > agent.matricule > agentId)
    const finalAgentId = String(agent.id || agent.matricule || agentId)
    
    console.log(`🔍 Identifiant final utilisé: ${finalAgentId}`)
    
    // Vérifier si l'agent a déjà une carte enregistrée (chercher par ID ou matricule)
    const agentIndex = data.agents.findIndex(a => 
      String(a.agentId) === finalAgentId || 
      String(a.id) === finalAgentId ||
      String(a.matricule) === String(agent.matricule || agentId)
    )

    // Créer l'entrée avec TOUTES les informations de l'agent
    const agentCardInfo = {
      agentId: finalAgentId,
      id: finalAgentId,
      matricule: agent.matricule || '',
      nom: agent.nom || '',
      prenom: agent.prenom || '',
      sexe: agent.sexe || '',
      date_naissance: agent.date_naissance || '',
      email: agent.email || '',
      telephone: agent.telephone || '',
      direction: agent.direction || '',
      direction_id: agent.direction_id || null,
      fonction: agent.fonction || '',
      fonction_id: agent.fonction_id || null,
      grade: agent.grade || '',
      grade_id: agent.grade_id || null,
      adresse: agent.adresse || '',
      cardNumber: cardNumber,
      capturedDate: new Date().toISOString()
    }

    if (agentIndex === -1) {
      // Nouvel agent : l'ajouter à la liste
      data.agents.push(agentCardInfo)
      console.log(`✅ Nouvel agent ajouté avec numéro ${cardNumber}:`, agentCardInfo)
    } else {
      // Agent existant : mettre à jour ses informations
      data.agents[agentIndex] = agentCardInfo
      console.log(`✅ Agent mis à jour avec numéro ${cardNumber}:`, agentCardInfo)
    }
    
    // Mettre à jour le dernier numéro utilisé
    if (cardNumber > data.lastNumber) {
      data.lastNumber = cardNumber
    }

    const newData = {
      year: CURRENT_YEAR,
      lastNumber: data.lastNumber,
      agents: data.agents
    }
    
    // Sauvegarder dans localStorage (synchronisé avec le fichier centralisé)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newData))
    console.log(`💾 Carte capturée sauvegardée dans localStorage`)
    console.log(`💾 Année: ${newData.year}, Dernier numéro: ${newData.lastNumber}`)
    console.log(`💾 Nombre d'agents: ${newData.agents.length}`)
    console.log(`💾 Agents:`, newData.agents)
    console.log(`💾 Les données sont synchronisées avec le fichier public/data/cartes_service.json`)

    return true
  } catch (error) {
    console.error('❌ Erreur dans saveCapturedCard:', error)
    return false
  }
}

/**
 * Obtient les données actuelles pour affichage/export
 * Les données sont synchronisées avec le fichier public/data/cartes_service.json
 * Note: Le fichier est chargé au démarrage et les données sont sauvegardées dans localStorage
 * Pour mettre à jour le fichier, il faut le faire manuellement ou via un backend API
 * @returns {Object|null} Les données actuelles ou null si erreur
 */
export const getCardDataForExport = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) {
      console.error('❌ Aucune donnée trouvée dans localStorage')
      return null
    }
    
    const data = JSON.parse(stored)
    
    // Vérifier que les données sont valides
    if (!data || !data.agents) {
      console.error('❌ Données invalides dans localStorage:', data)
      return null
    }
    
    return data
  } catch (error) {
    console.error('❌ Erreur lors de la récupération des données:', error)
    return null
  }
}

/**
 * Charge les données depuis le fichier JSON centralisé
 * @returns {Promise<Object|null>} Les données chargées ou null si erreur
 */
export const loadCardsFromCentralFile = async () => {
  try {
    const data = await loadCardsFromFile()
    if (data) {
      console.log(`✅ Données chargées depuis le fichier centralisé: ${data.agents.length} agents`)
      // Synchroniser avec localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
      return data
    }
    return null
  } catch (error) {
    console.error('❌ Erreur lors du chargement depuis le fichier:', error)
    return null
  }
}

/**
 * Initialise les données depuis le fichier centralisé au démarrage
 * Cette fonction doit être appelée au démarrage de l'application
 */
export const initializeCardsFromFile = async () => {
  try {
    const fileData = await loadCardsFromCentralFile()
    if (fileData) {
      console.log(`✅ Données initialisées depuis le fichier centralisé`)
      return fileData
    } else {
      // Si le fichier n'existe pas, utiliser localStorage
      const localData = getCardNumberData()
      console.log(`ℹ️ Utilisation des données de localStorage`)
      return localData
    }
  } catch (error) {
    console.error('❌ Erreur lors de l\'initialisation:', error)
    return getCardNumberData()
  }
}

/**
 * Obtient le numéro de carte actuel sans l'incrémenter
 * @returns {number} Le numéro de carte actuel
 */
export const getCurrentCardNumber = () => {
  const data = getCardNumberData()
  return data.lastNumber
}

/**
 * Réinitialise le compteur de numéros de carte pour l'année courante
 * Remet le compteur à 0, donc le prochain numéro sera 1
 * @param {boolean} clearAgents - Si true, efface aussi la liste des agents
 */
export const resetCardNumbers = (clearAgents = false) => {
  const newData = { 
    year: CURRENT_YEAR, 
    lastNumber: 0,
    agents: clearAgents ? [] : getCardNumberData().agents
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(newData))
  console.log('Compteur de cartes réinitialisé. Le prochain numéro sera 1.')
  return newData
}

/**
 * Force la réinitialisation du compteur (utile pour recommencer à 1)
 */
export const forceResetToStart = () => {
  return resetCardNumbers()
}

/**
 * Vérifie et réinitialise si nécessaire (pour forcer le démarrage à 1)
 * Utile si vous voulez recommencer la numérotation
 */
export const checkAndResetIfNeeded = () => {
  const data = getCardNumberData()
  // Si le dernier numéro est supérieur à 0, on peut réinitialiser
  // Cette fonction peut être appelée manuellement si besoin
  if (data.lastNumber > 0) {
    console.warn(`Le compteur actuel est à ${data.lastNumber + 1}. Appelez resetCardNumbers() pour réinitialiser à 1.`)
  }
  return data
}

/**
 * Définit manuellement le numéro de carte
 * @param {number} number - Le numéro à définir
 */
export const setCardNumber = (number) => {
  if (typeof number !== 'number' || number < 0) {
    throw new Error('Le numéro de carte doit être un nombre positif')
  }
  const data = getCardNumberData()
  const newData = { 
    year: CURRENT_YEAR, 
    lastNumber: number,
    agents: data.agents || []
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(newData))
  return newData
}

/**
 * Obtient la liste de tous les agents qui ont reçu une carte
 * @returns {Array} Liste des agents avec leurs informations de carte
 */
export const getAllAgentsWithCards = () => {
  const data = getCardNumberData()
  return data.agents || []
}

/**
 * Obtient la liste des agents qui ont imprimé leur carte
 * @returns {Array} Liste des agents qui ont imprimé
 */
export const getPrintedCards = () => {
  const data = getCardNumberData()
  return (data.agents || []).filter(a => a.printed === true)
}

/**
 * Obtient la liste des agents qui n'ont pas encore imprimé leur carte
 * @returns {Array} Liste des agents qui n'ont pas imprimé
 */
export const getUnprintedCards = () => {
  const data = getCardNumberData()
  return (data.agents || []).filter(a => a.printed !== true)
}

/**
 * Supprime un agent de la liste (si erreur d'assignation)
 * @param {string|number} agentId - L'ID de l'agent à supprimer
 * @returns {boolean} True si la suppression a réussi
 */
export const removeAgentCard = (agentId) => {
  const data = getCardNumberData()
  const initialLength = data.agents.length
  data.agents = data.agents.filter(a => 
    a.agentId !== String(agentId) && 
    a.agentId !== Number(agentId) &&
    a.id !== String(agentId) &&
    a.id !== Number(agentId)
  )

  if (data.agents.length < initialLength) {
    const newData = {
      year: CURRENT_YEAR,
      lastNumber: data.lastNumber,
      agents: data.agents
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newData))
    return true
  }
  return false
}

/**
 * Exporte les données de numérotation (pour sauvegarde/backup)
 * @returns {Object} Les données actuelles
 */
export const exportCardNumberData = () => {
  return getCardNumberData()
}

/**
 * Importe les données de numérotation (pour restauration)
 * @param {Object} data - Les données à importer { year, lastNumber, agents }
 */
export const importCardNumberData = (data) => {
  if (!data || typeof data.year !== 'number' || typeof data.lastNumber !== 'number') {
    throw new Error('Format de données invalide')
  }
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  return data
}
